import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { TokenService } from '../../service/token/token.service';
import { NavigationService } from '../../service/navigation/navigation.service';
import { CommonService, DataService } from '../../core.index';
import { AuthService } from '../../../auth/auth.service';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AccountService } from '../../../modules/account/account.service';
import { catchError, finalize, switchMap, tap } from 'rxjs';

interface LoginResponse {
  access_token: string;
  token_type: string;
  expires_in: string;
  refresh_token?: string;
}

@Injectable({
  providedIn: 'root',
})
export class HomeGuard {
  private isProcessingToken = false;
  orgUnits: any[] = [];

  constructor(
    private navigationService: NavigationService,
    private commonService: CommonService,
    private tokenService: TokenService,
    private authService: AuthService,
    private accountService: AccountService,
    private router: Router,
    private dataService: DataService,
  ) { }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {

    const workerToken = this.commonService.worker_token;
    const w_lang = localStorage.getItem('worker_language');
    // const plantIdMatch = state.url.match(/\/employee\/(\d+)\//);
    // const currentPlantId = plantIdMatch ? plantIdMatch[1] : null;

    if (workerToken) {
      try {
        return of(true);
      } catch (e) {
        return of(false);
      }
    } else {
      if (w_lang && w_lang !== '' && w_lang !== null && w_lang !== 'undefined') {
        this.router.navigate([`/grievance_link`]);
        localStorage.removeItem('worker_language');
        return of(false);
      }
    }

    // Handle JWT code login
    const url = new URL(window.location.href);
    const code = url.searchParams.get('code');
    const isRaiseGrv = url.searchParams.get('raise_grievance');
    if (code && !this.isProcessingToken) {
      return this.handleLoginWithCode(code, isRaiseGrv);
    }

    if (!this.tokenService.isLoggedIn()) {
      // Store the intended URL before redirecting to login
      if (state.url !== '/' && state.url !== '') {
        localStorage.setItem('returnUrl', state.url);
      }
      this.navigationService.navigateTo('/login');
      return of(false);
    }

    // Check if profile is already loaded
    if (!this.commonService.getProfileSnapshot()) {
      return new Observable<boolean>((observer) => {
        this.accountService.getProfile().subscribe({
          next: (profileData) => {
            this.commonService.setProfile(profileData);
            // Check permissions after profile load
            const hasAccess = this.checkRouteAccessWithFallback(state.url, profileData);
            observer.next(hasAccess);
            observer.complete();
          },
          error: (error) => {
            console.error('Error fetching profile:', error);
            this.navigationService.navigateTo('/login');
            observer.next(false);
            observer.complete();
          },
        });
      });
    } else {
      // Check permissions if profile already loaded
      const profile = this.commonService.getProfileSnapshot();
      const hasAccess = this.checkRouteAccessWithFallback(state.url, profile);
      return of(hasAccess);
    }
  }

  /**
   * Check route access and handle fallback logic for Grievance module
   * Returns true if access allowed, false if redirected
   */
  private checkRouteAccessWithFallback(url: string, profile: any): boolean {
    // If user can access the route, allow
    if (this.commonService.canAccessRoute(url)) {
      return true;
    }

    // If route is in grievance module and user doesn't have access
    if (url.includes('/grievance')) {
      const moduleNames = profile?.roles?.module_names || [];
      const isSelfMode = localStorage.getItem('self') === 'true';

      // Check if user has access to grievance module
      if (!moduleNames.includes('grievance')) {
        // If self mode is enabled, show no-permission page
        if (isSelfMode) {
          this.router.navigate(['/grievance/no-permission']);
          return false;
        }
        // User doesn't have grievance module access, redirect to employee portal
        const orgUnits = profile?.org_units || [];
        if (orgUnits.length > 0) {
          this.router.navigate(['/grievance/employee/portal']);
        } else {
          this.router.navigate(['/apps']);
        }
        return false;
      }

      // User has module access but not permission for this specific route
      // Show no-permission page
      this.router.navigate(['/grievance/no-permission']);
      return false;
    }

    // Handle conference-room routes specifically: redirect to conference dashboard
    if (url.includes('/conference-room')) {
      this.router.navigate(['/conference-room/dashboard']);
      return false;
    }

    // For other non-grievance routes, use existing logic
    const orgUnits = profile?.org_units || [];
    if (orgUnits.length > 0) {
      this.router.navigate(['/grievance/employee/portal']);
    } else {
      this.router.navigate(['/apps']);
    }
    return false;
  }

  private handleLoginWithCode(code: string, isRaiseGrv: string | null): Observable<boolean> {
    this.isProcessingToken = true;
    this.removeCodeFromUrl();
    this.tokenService.removeTokens();
    localStorage.removeItem('userProfile');
    localStorage.removeItem('orgUnits');
    localStorage.removeItem('access_token');
    localStorage.removeItem('REFRESH_TOKEN');
    localStorage.removeItem('TOKEN_TYPE');
    localStorage.removeItem('EXPIRE_IN');

    return this.authService.loginWithCode(code).pipe(
      switchMap((response: any) => {
        const tokenData: LoginResponse = {
          access_token: response.access_token || response.accessToken || code,
          token_type: response.token_type || response.tokenType || 'Bearer',
          expires_in: response.expires_in || response.expiresIn || '3600',
          refresh_token: response.refresh_token || response.refreshToken
        };

        this.tokenService.storeTokens(tokenData);

        return this.accountService.getProfile().pipe(
          tap((profileData) => {
            localStorage.setItem('userProfile', JSON.stringify(profileData));
            this.orgUnits = profileData.org_units || [];
            this.dataService.setOrgUnits(this.orgUnits);
            localStorage.setItem('orgUnits', JSON.stringify(this.orgUnits));

            if (window.location.pathname.includes('/grievance')) {
              if (isRaiseGrv === 'true') {
                this.router.navigate(['/grievance/employee/portal']);
              }
              if (localStorage.getItem('self') === 'true' && this.orgUnits.length > 0) {
                this.router.navigate(['/grievance/employee/portal']);
              } else {
                this.router.navigate(['/grievance/dashboard']);
              }
            }
          }),
          switchMap(() => of(false)) // Prevent route activation
        );
      }),
      catchError((error) => {
        console.error('Login error:', error);
        this.router.navigate(['/code-login-error']);
        return of(false);
      }),
      finalize(() => {
        this.isProcessingToken = false;
      })
    );
  }

  private removeCodeFromUrl(): void {
    if (window.history && window.history.replaceState) {
      const url = new URL(window.location.href);
      if (url.searchParams.has('code')) {
        url.searchParams.delete('code');
        const cleanUrl = url.toString();
        window.history.replaceState({}, document.title, cleanUrl);
      }
    }
  }
}
